"use client";

import { useEffect, useState } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";
import {
  isClientSubscribed,
  onClientSubscribed,
} from "@/lib/subscription-client";
import { useExploreRoute } from "../_lib/explore-route-context";

const OTHER_EDITION: Record<ReaderReportType, ReaderReportType> = {
  engineer: "creative",
  creative: "engineer",
};

const EDITION_LABEL: Record<ReaderReportType, string> = {
  engineer: "Engineer",
  creative: "Creative",
};

interface SubscriptionStatus {
  /** The edition for the current page */
  currentEdition: ReaderReportType;
  /** Whether user is subscribed to current page's edition */
  subscribedToCurrent: boolean;
  /** Whether user is subscribed to the other edition */
  subscribedToOther: boolean;
  /** The other edition key */
  otherEdition: ReaderReportType;
  /** Human label for current edition */
  currentLabel: string;
  /** Human label for the other edition */
  otherLabel: string;
}

export function useSubscriptionStatus(): SubscriptionStatus {
  const { reportType: currentEdition } = useExploreRoute();
  const otherEdition = OTHER_EDITION[currentEdition];

  const [subscribedToCurrent, setSubscribedToCurrent] = useState(false);
  const [subscribedToOther, setSubscribedToOther] = useState(false);

  useEffect(() => {
    const refresh = () => {
      setSubscribedToCurrent(isClientSubscribed({ audience: currentEdition }));
      setSubscribedToOther(isClientSubscribed({ audience: otherEdition }));
    };

    // Initial read from cookie
    refresh();

    // Re-check when a subscription event fires (same session)
    const unsubscribe = onClientSubscribed(refresh);
    return unsubscribe;
  }, [currentEdition, otherEdition]);

  return {
    currentEdition,
    subscribedToCurrent,
    subscribedToOther,
    otherEdition,
    currentLabel: EDITION_LABEL[currentEdition],
    otherLabel: EDITION_LABEL[otherEdition],
  };
}
